import { useState } from 'react';

import { useDealEvidenceBody } from '../../hooks/useDeals';
import type { EvidenceItem } from '../../types';
import { EvidenceBody, evidenceBodyState } from '../shared/EvidenceBody';
import { formatEtShort } from './DealsPage.shared';
import { RefuteEvidenceModal } from './DealsPage.modals';
import styles from './DealsPage.module.css';

/** Evidence grid row, extracted verbatim from DealsPage.tsx
 *  (D4 componentization, zero behavior change). */

interface EvidenceRowProps {
  ev: EvidenceItem;
  dealKey: string;
}

/** Pillar F.5 — ONE evidence row's body, fetched because this component MOUNTED, which happens on the
 * click. This tab renders the same `EvidenceItem` rows out of the same loader as the CRM record panel,
 * so it gets the same discipline: no body text on the card payload, one per-item read on request. Its
 * door is the Deals twin (`/api/deal-cards/{key}/evidence/{id}/body`) rather than the CRM one because
 * this router applies this tab's gate. It is audited like the CRM one — same `crm_access_audit` lane,
 * `action='evidence_body'`, committed before the body returns — which matters because THIS is the door
 * most reads come through: the CRM record panel is allowlist-gated, this tab is not. */
function DealEvidenceBody({ dealKey, eventLogId }: { dealKey: string; eventLogId: number }) {
  const q = useDealEvidenceBody(dealKey, eventLogId, true);
  return (
    <EvidenceBody className={styles.evidenceBody} state={evidenceBodyState(q)} body={q.data?.body} />
  );
}

// Slice J §J5 — evidence row as Subject / From / To / Time grid.
// F.5 (2026-08-20) — `body_available` is server-gated (admin OR inbox-owner: the same gate that used
// to populate the removed inline `body_preview`). When true, the row gets an expand chevron and the
// body is fetched on the click; the card payload carries no body text at all.
//
// 2026-05-25 dedup — `inbox_count > 1` renders an "+N inboxes" badge in
// the Subject cell so the user knows the row is one email seen by
// multiple Ember mailboxes (not N separate emails).
//
// 2026-05-26 refute — per-row flag icon opens RefuteModal so operators
// can mark wrong evidence noise / move it to a different deal.
//
// E1b-0 (2026-08-21) — the icon is gated on the server's `can_refute` (`may_refute_event` for this
// row and this caller), because this row and the CRM's two render the SAME dataclass and a gate that
// differs per surface is not a gate. Concretely it stops offering a ⚑ that the door already refused
// with a 403: a viewer outside the deal's audience who is not the event's inbox owner — mostly a
// MEETING row, which has no inbox owner and is shown to everybody by §9.
export function EvidenceRow({ ev, dealKey }: EvidenceRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [refuteOpen, setRefuteOpen] = useState(false);
  const canExpand = ev.body_available;
  const time = ev.occurred_at ? formatEtShort(ev.occurred_at) : '—';
  const toFmt =
    ev.recipients_to.length === 0
      ? '—'
      : ev.recipients_to.length <= 2
        ? ev.recipients_to.join(', ')
        : `${ev.recipients_to.slice(0, 2).join(', ')} +${ev.recipients_to.length - 2}`;
  return (
    <>
      <div
        className={styles.evidenceGridRow}
        onClick={canExpand ? () => setExpanded((e) => !e) : undefined}
        style={{ cursor: canExpand ? 'pointer' : 'default' }}
        data-evidence-toggle={canExpand ? ev.event_log_id : undefined}
        aria-expanded={canExpand ? expanded : undefined}
      >
        <span className={styles.evidenceChevron}>
          {canExpand ? (expanded ? '▾' : '▸') : ''}
        </span>
        <span className={styles.evidenceSubjectCell}>
          {ev.subject || <em style={{ color: '#999' }}>(no subject)</em>}
          {ev.inbox_count > 1 && (
            <span
              className={styles.evidenceInboxCountBadge}
              title={`Same email scanned from ${ev.inbox_count} Ember mailboxes`}
            >
              +{ev.inbox_count - 1} inbox{ev.inbox_count - 1 === 1 ? '' : 'es'}
            </span>
          )}
        </span>
        <span className={styles.evidenceFromCell}>{ev.sender ?? '—'}</span>
        <span
          className={styles.evidenceToCell}
          title={ev.recipients_to.join(', ')}
        >
          {toFmt}
        </span>
        <span className={styles.evidenceTimeCell}>{time}</span>
        {ev.can_refute && (
          <button
            type="button"
            className={styles.refuteBtn}
            title="Refute evidence"
            onClick={(e) => {
              e.stopPropagation();
              setRefuteOpen(true);
            }}
          >
            ⚑
          </button>
        )}
      </div>
      {expanded && canExpand && (
        <DealEvidenceBody dealKey={dealKey} eventLogId={ev.event_log_id} />
      )}
      {refuteOpen && (
        <RefuteEvidenceModal
          ev={ev}
          dealKey={dealKey}
          onClose={() => setRefuteOpen(false)}
        />
      )}
    </>
  );
}
